<template>
  <div class="image-gallery">
    <el-dialog v-model="visible" fullscreen :show-close="true" :close-on-click-modal="true" class="image-gallery-dialog" @close="handleClose">
      <div class="gallery-container">
        <!-- 大图区域 -->
        <div class="main-image-wrapper">
          <Swiper
            :modules="modules"
            :slides-per-view="1"
            :space-between="0"
            :zoom="true"
            :keyboard="{
              enabled: true
            }"
            :initial-slide="currentImageIndex"
            @swiper="onMainSwiperInit"
            @slide-change="onSlideChange"
            class="main-swiper"
          >
            <SwiperSlide v-for="(image, index) in images" :key="index">
              <div class="swiper-zoom-container">
                <el-image :src="image" fit="scale-down" class="large-image" />
              </div>
            </SwiperSlide>
          </Swiper>

        </div>

        <!-- 标题 -->
        <h3 class="gallery-title" v-if="title">{{ title }}</h3>

        <!-- 缩略图区域 -->
        <div class="thumbnails-wrapper" v-if="images.length > 1">
          <div ref="thumbnailsContainer" class="thumbnails-container" @scroll="onThumbnailScroll">
            <div class="thumbnails">
              <div v-for="(image, index) in visibleThumbnails" :key="index" :ref="el => setThumbnailRef(el, index)" class="thumbnail" :class="{ active: currentImageIndex === index }" @click="setCurrentImage(index)">
                <el-image :src="image" fit="cover" :alt="`Thumbnail ${index + 1}`" class="thumbnail-image" :lazy="true" />
              </div>
            </div>
          </div>

          <button class="thumb-nav-btn thumb-nav-prev" v-if="images.length > 5 && canScrollLeft" @click="scrollThumbnails('left')">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button class="thumb-nav-btn thumb-nav-next" v-if="images.length > 5 && canScrollRight" @click="scrollThumbnails('right')">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
  import { Swiper, SwiperSlide } from 'swiper/vue'
  import { Navigation, Zoom, Keyboard } from 'swiper/modules'
  import 'swiper/css'
  import 'swiper/css/navigation'
  import 'swiper/css/zoom'

  const props = defineProps({
    modelValue: {
      type: Boolean,
      default: false
    },
    images: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    initialIndex: {
      type: Number,
      default: 0
    }
  })

  const emit = defineEmits(['update:modelValue', 'close'])

  const modules = [Navigation, Zoom, Keyboard]

  const visible = computed({
    get: () => props.modelValue,
    set: val => emit('update:modelValue', val)
  })

  const currentImageIndex = ref(0)
  const mainSwiper = ref(null)
  const thumbnailsContainer = ref(null)
  const thumbnailRefs = ref([])
  const canScrollLeft = ref(false)
  const canScrollRight = ref(false)
  const loadedThumbnails = ref(new Set())

  // 计算可见缩略图（虚拟滚动优化）
  const visibleThumbnails = computed(() => props.images)

  // Swiper 实例初始化
  const onMainSwiperInit = swiper => {
    mainSwiper.value = swiper
  }

  // 监听滑动变化
  const onSlideChange = swiper => {
    currentImageIndex.value = swiper.activeIndex
    scrollToActiveThumbnail()
  }

  // 设置当前图片
  const setCurrentImage = index => {
    currentImageIndex.value = index
    if (mainSwiper.value) {
      mainSwiper.value.slideTo(index, 300)
    }
  }

  // 设置缩略图引用
  const setThumbnailRef = (el, index) => {
    if (el) {
      thumbnailRefs.value[index] = el
    }
  }

  // 滚动到激活的缩略图
  const scrollToActiveThumbnail = () => {
    if (!thumbnailsContainer.value || !thumbnailRefs.value[currentImageIndex.value]) return

    const container = thumbnailsContainer.value
    const activeThumb = thumbnailRefs.value[currentImageIndex.value]

    if (activeThumb) {
      const containerWidth = container.clientWidth
      const thumbLeft = activeThumb.offsetLeft
      const thumbWidth = activeThumb.offsetWidth
      const scrollLeft = thumbLeft - containerWidth / 2 + thumbWidth / 2

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      })
    }
  }

  // 缩略图滚动事件
  const onThumbnailScroll = () => {
    if (!thumbnailsContainer.value) return

    const container = thumbnailsContainer.value
    canScrollLeft.value = container.scrollLeft > 0
    canScrollRight.value = container.scrollLeft < container.scrollWidth - container.clientWidth - 1
  }

  // 滚动缩略图
  const scrollThumbnails = direction => {
    if (!thumbnailsContainer.value) return

    const container = thumbnailsContainer.value
    const scrollAmount = 300

    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    })
  }

  // 监听初始索引变化
  watch(
    () => props.initialIndex,
    newVal => {
      currentImageIndex.value = newVal
      if (mainSwiper.value) {
        mainSwiper.value.slideTo(newVal, 0)
      }
    },
    { immediate: true }
  )

  // 监听显示状态
  watch(
    () => props.modelValue,
    async newVal => {
      if (newVal) {
        currentImageIndex.value = props.initialIndex
        if (mainSwiper.value) {
          mainSwiper.value.slideTo(props.initialIndex, 0)
        }
        // 等待 DOM 更新后初始化滚动状态
        await nextTick()
        onThumbnailScroll()
        scrollToActiveThumbnail()
      } else {
        // 关闭时清理已加载标记，释放内存
        loadedThumbnails.value.clear()
      }
    }
  )

  // 监听当前索引变化，预加载相邻图片
  watch(currentImageIndex, () => {
    // 预加载当前图片前后各1张
    const preloadRange = 1
    for (let i = -preloadRange; i <= preloadRange; i++) {
      const index = currentImageIndex.value + i
      if (index >= 0 && index < props.images.length) {
        loadedThumbnails.value.add(index)
      }
    }
  })

  // 关闭处理
  const handleClose = () => {
    emit('close')
  }

  // 组件挂载时初始化滚动监听
  onMounted(() => {
    if (thumbnailsContainer.value) {
      onThumbnailScroll()
    }
  })

  // 组件卸载时清理
  onUnmounted(() => {
    thumbnailRefs.value = []
    loadedThumbnails.value.clear()
  })
</script>

<style scoped lang="less">
  :deep(.image-gallery-dialog) {
    padding: 0;
    background: transparent;
    .el-dialog__header {
      position: absolute;
      right: 0;
      top: 0;
      padding: 20px;
      zoom: 1.2;
      z-index: 10;
      display: flex;
    }

    .el-dialog__body {
      padding: 0;
      background-color: rgba(0, 0, 0, 0.85);
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    .el-dialog__headerbtn {
      top: 5px;
      right: 5px;
      font-size: 30px;

      .el-dialog__close {
        color: white;
        font-weight: bold;
      }
    }
  }

  .gallery-container {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 30px;
    padding: 40px 0;
    overflow: hidden;
    box-sizing: border-box;
  }

  .main-image-wrapper {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 20px;
    height: calc(100vh - 250px);
    position: relative;
  }

  .main-swiper {
    width: 100%;
    height: 100%;
  }

  .swiper-zoom-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .large-image {
    width: 100%;
    height: 100%;
    max-height: 100%;
  }

  .gallery-title {
    font-size: 28px;
    font-weight: 600;
    color: white;
    text-align: center;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }

  .thumbnails-wrapper {
    width: 85vw;
    max-width: 1200px;
    flex-shrink: 0;
    position: relative;
    padding: 0 60px;
  }

  .thumbnails-container {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-behavior: smooth;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */

    &::-webkit-scrollbar {
      display: none; /* Chrome/Safari */
    }
  }

  .thumbnails {
    display: flex;
    gap: 15px;
    justify-content: center;
    min-width: min-content;
    padding: 10px;
  }

  .thumbnail {
    width: 120px;
    height: 120px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    border: 3px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    flex-shrink: 0;
    will-change: transform;
  }

  .thumbnail-placeholder {
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.1);
  }

  .thumbnail-image {
    width: 100%;
    height: 100%;
    display: block;
  }

  .thumb-nav-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.9);
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .thumb-nav-prev {
    left: 10px;
  }

  .thumb-nav-next {
    right: 10px;
  }

  .thumb-nav-btn:hover {
    background-color: white;
    transform: translateY(-50%) scale(1.1);
  }

  .thumb-nav-btn svg {
    width: 20px;
    height: 20px;
    color: #333;
  }

  .thumbnail:hover {
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.05);
  }

  .thumbnail.active {
    border-color: white;
    box-shadow: 0 0 0 2px white, 0 4px 12px rgba(255, 255, 255, 0.5);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    .gallery-container {
      gap: 20px;
      padding: 20px 10px;
    }

    .main-image-wrapper {
      max-height: calc(100vh - 200px);
    }

    .gallery-title {
      font-size: 22px;
    }

    .thumbnail {
      width: 90px;
      height: 90px;
    }

    .thumbnails-wrapper {
      padding: 5px 0;
    }

    .thumbnails-container {
      padding: 0 50px;
    }

    .thumb-nav-btn {
      width: 36px;
      height: 36px;
    }

    .thumb-nav-btn svg {
      width: 18px;
      height: 18px;
    }
  }
</style>
